<template>
  <div>
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="ShieldIcon"
            size="20"
            class="text-primary mr-1"
          />
          <h4 class="mb-0">
            Position Guardian
          </h4>
        </div>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="loading"
          @click="refreshAll"
        >
          <feather-icon icon="RefreshCwIcon" /> Yenile
        </b-button>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <div class="text-muted font-small-3">
              Açık pozisyon
            </div>
            <h4>{{ counts.open || 0 }}</h4>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <div class="text-muted font-small-3">
              Guardian aktif
            </div>
            <h4>{{ counts.active || 0 }}</h4>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <div class="text-muted font-small-3">
              Artı pozisyon
            </div>
            <h4 class="text-success">
              {{ counts.positive || 0 }}
            </h4>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-1"
          >
            <div class="text-muted font-small-3">
              Eksi pozisyon
            </div>
            <h4 class="text-danger">
              {{ counts.negative || 0 }}
            </h4>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-tabs pills>
      <b-tab
        title="Genel Bakış"
        active
      >
        <b-card no-body>
          <b-table
            :items="positions"
            :fields="positionFields"
            responsive
            small
            show-empty
            empty-text="Açık pozisyon yok."
          >
            <template #cell(position)="data">
              <strong>{{ data.item.position.symbol }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.bot.name }} · {{ data.item.position.side }}
              </div>
            </template>
            <template #cell(pnl)="data">
              <span :class="data.item.pnlUsdt >= 0 ? 'text-success' : 'text-danger'">
                {{ signedNum(data.item.pnlUsdt, 2) }} USDT
              </span>
              <div class="text-muted font-small-3">
                {{ signedNum(data.item.pnlPct, 2) }}%
              </div>
            </template>
            <template #cell(excursion)="data">
              MFE {{ formatNum(data.item.mfePct, 2) }}%
              <div class="text-muted font-small-3">
                MAE {{ formatNum(data.item.maePct, 2) }}%
              </div>
            </template>
            <template #cell(risk)="data">
              Giveback {{ formatNum(data.item.givebackPercent, 0) }}%
              <div class="text-muted font-small-3">
                {{ formatNum(data.item.ageMinutes, 0) }} dk
              </div>
            </template>
            <template #cell(config)="data">
              <b-badge :variant="data.item.config.enabled ? 'success' : 'secondary'">
                {{ data.item.config.enabled ? 'Aktif' : 'Kapalı' }}
              </b-badge>
              <div class="text-muted font-small-3">
                {{ data.item.config.authority }} · {{ data.item.config.profile }}
              </div>
              <div class="text-muted font-small-3">
                {{ data.item.config.walletAware ? 'Cüzdan duyarlı' : 'Cüzdan kapalı' }} · {{ data.item.config.tpPreservation ? 'TP koruma' : 'TP koruma kapalı' }}
              </div>
              <div class="text-muted font-small-3">
                {{ data.item.config.halfSlSoftExitEnabled ? 'Yarı SL ' + formatNum(data.item.config.halfSlSoftExitRatio, 0) + '%' : 'Yarı SL kapalı' }}
              </div>
              <div class="text-muted font-small-3">
                {{ data.item.config.avgWinProtectionEnabled ? 'Avg win ' + formatNum(data.item.config.minProfitCloseAvgWinRatio, 0) + '%' : 'Avg win kapalı' }}
              </div>
              <div class="text-muted font-small-3">
                {{ data.item.config.recoveryAssistEnabled ? 'Recovery açık' : 'Recovery kapalı' }}
              </div>
              <div
                v-for="line in getStrategySafetyGovernorCoordinationSummary(data.item.config)"
                :key="line"
                class="text-muted font-small-3"
              >
                {{ line }}
              </div>
              <b-badge
                variant="light-primary"
                class="mt-25"
              >
                {{ getStrategySafetyGovernorCoordinationDecision(data.item.config).kindLabel }} · {{ getStrategySafetyGovernorCoordinationDecision(data.item.config).label }} · {{ getStrategySafetyGovernorCoordinationDecision(data.item.config).text }}
              </b-badge>
              <div class="text-muted font-small-3">
                {{ getStrategySafetyGovernorCoordinationState(data.item.config).decisionSummary }}
              </div>
              <div class="text-muted font-small-3">
                {{ getStrategySafetyGovernorCoordinationDecision(data.item.config).detail }}
              </div>
              <div
                v-for="line in getStrategySafetyGovernorCoordinationState(data.item.config).axisReasons"
                :key="`axis-${line.code}`"
                class="text-muted font-small-3"
              >
                {{ line.label }} · {{ line.text }}
              </div>
              <div
                v-for="line in getStrategySafetyGovernorConflictSummary(data.item.config)"
                :key="line.code"
                class="text-muted font-small-3"
              >
                {{ line.code }} · {{ line.text }}
              </div>
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="closePosition(data.item)"
              >
                Kapat
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <b-tab title="Hedefler">
        <b-card no-body>
          <b-alert
            v-if="tuneResult"
            show
            dismissible
            :variant="tuneResult.applied ? 'success' : 'info'"
            class="m-1"
            @dismissed="tuneResult = null"
          >
            <strong>{{ tuneResult.targetName }}</strong>
            {{ tuneResult.applied ? 'veriye göre otomatik güncellendi.' : 'için veriye göre öneri üretildi.' }}
            <div class="font-small-3 mt-50">
              İşlem {{ formatNum(tuneResult.metrics.trades, 0) }} · WR {{ formatNum(tuneResult.metrics.winRate, 1) }}% · PF {{ formatNum(tuneResult.metrics.profitFactor, 2) }} · Avg win {{ signedNum(tuneResult.metrics.avgWin, 2) }} · Avg loss {{ signedNum(tuneResult.metrics.avgLoss, 2) }}
            </div>
            <div class="font-small-3">
              Onerilen mod: <strong>{{ modeLabel(tuneResult.recommendedMode) }}</strong> · {{ tuneResult.modeReason }}
            </div>
            <div class="font-small-3">
              Öneri: Komisyonu kapat {{ formatNum(tuneResult.suggested.breakEvenMoveMfe, 2) }} · Kârı kilitle {{ formatNum(tuneResult.suggested.profitMoveMfe, 2) }} · Min kilit {{ formatNum(tuneResult.suggested.profitMoveMinBuffer, 2) }} · Kâr koruma {{ formatNum(tuneResult.suggested.minMfeToProtect, 2) }} · Geri verme {{ formatNum(tuneResult.suggested.maxGivebackPercent, 0) }}% · Avg win hedef {{ formatNum(tuneResult.suggested.minProfitCloseAvgWinRatio, 0) }}% · İzleme {{ formatNum(tuneResult.suggested.mfeProgressWatchStart, 2) }} / {{ formatNum(tuneResult.suggested.mfeProgressWatchMinutes, 0) }} dk
            </div>
            <div
              v-if="tuneResult.notes && tuneResult.notes.length"
              class="font-small-3"
            >
              {{ tuneResult.notes.join(' ') }}
            </div>
          </b-alert>
          <b-table
            :items="targets"
            :fields="targetFields"
            responsive
            small
            show-empty
            empty-text="Hedef bulunamadı."
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.status }}
              </div>
            </template>
            <template #cell(enabled)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.enabled"
                switch
                @change="toggleTarget(data.item, $event)"
              >
                Position Guardian
              </b-form-checkbox>
            </template>
            <template #cell(authority)="data">
              <b-form-select
                :value="data.item.positionGuardian.authority"
                :options="authorityOptions"
                size="sm"
                @change="updateTarget(data.item, { authority: $event })"
              />
            </template>
            <template #cell(profile)="data">
              <b-form-select
                :value="data.item.positionGuardian.profile"
                :options="profileOptions"
                size="sm"
                @change="updateTarget(data.item, { profile: $event })"
              />
            </template>
            <template #cell(mode)="data">
              <b-badge variant="light-primary">
                {{ modeLabel(data.item.exitGovernorMode) }}
              </b-badge>
            </template>
            <template #cell(coordinate)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.coordinateMfeControls !== false"
                switch
                @change="updateTarget(data.item, { coordinateMfeControls: $event })"
              >
                Koruma çakışmasını önle
              </b-form-checkbox>
            </template>
            <template #cell(cost)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.walletAware !== false"
                switch
                @change="updateTarget(data.item, { walletAware: $event })"
              >
                Cüzdan duyarlı
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.tpPreservation !== false"
                switch
                @change="updateTarget(data.item, { tpPreservation: $event })"
              >
                TP koruma
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.oppositePnlFuseEnabled === true"
                switch
                @change="updateTarget(data.item, { oppositePnlFuseEnabled: $event })"
              >
                Karşıt PnL sigortası
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.halfSlSoftExitEnabled !== false"
                switch
                @change="updateTarget(data.item, { halfSlSoftExitEnabled: $event })"
              >
                Yarı SL
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.avgWinProtectionEnabled !== false"
                switch
                @change="updateTarget(data.item, { avgWinProtectionEnabled: $event })"
              >
                Avg win
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.mfeProgressWatchEnabled !== false"
                switch
                @change="updateTarget(data.item, { mfeProgressWatchEnabled: $event })"
              >
                TP ilerleme takibi
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.autoTuneApplyWithoutApproval === true"
                switch
                @change="updateTarget(data.item, { autoTuneApplyWithoutApproval: $event })"
              >
                Oto uygula
              </b-form-checkbox>
            </template>
            <template #cell(profitRules)="data">
              <div class="text-muted font-small-3 mb-50">
                PnL değil, fiyatın lehe ilerleme yüzdesi.
              </div>
              <div class="guardian-rule-row">
                <span>Komisyonu kapat</span>
                <b-form-input
                  :value="data.item.positionGuardian.breakEvenMoveMfe"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateTarget(data.item, { breakEvenMoveMfe: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kârı kilitle</span>
                <b-form-input
                  :value="data.item.positionGuardian.profitMoveMfe"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateTarget(data.item, { profitMoveMfe: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Min kilit</span>
                <b-form-input
                  :value="data.item.positionGuardian.profitMoveMinBuffer"
                  type="number"
                  size="sm"
                  step="0.01"
                  @change="updateTarget(data.item, { profitMoveMinBuffer: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kâr koruma</span>
                <b-form-input
                  :value="data.item.positionGuardian.minMfeToProtect"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateTarget(data.item, { minMfeToProtect: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Geri verme</span>
                <b-form-input
                  :value="data.item.positionGuardian.maxGivebackPercent"
                  type="number"
                  size="sm"
                  step="1"
                  @change="updateTarget(data.item, { maxGivebackPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Avg win hedef</span>
                <b-form-input
                  :value="data.item.positionGuardian.minProfitCloseAvgWinRatio"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateTarget(data.item, { minProfitCloseAvgWinRatio: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>İzlemeye al</span>
                <b-form-input
                  :value="data.item.positionGuardian.mfeProgressWatchStart"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateTarget(data.item, { mfeProgressWatchStart: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Süre dk</span>
                <b-form-input
                  :value="data.item.positionGuardian.mfeProgressWatchMinutes"
                  type="number"
                  size="sm"
                  step="1"
                  @change="updateTarget(data.item, { mfeProgressWatchMinutes: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Beklenen ilerleme</span>
                <b-form-input
                  :value="data.item.positionGuardian.mfeProgressMinBridgePercent"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateTarget(data.item, { mfeProgressMinBridgePercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Küçük zarar</span>
                <b-form-input
                  :value="data.item.positionGuardian.mfeProgressMaxLoss"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateTarget(data.item, { mfeProgressMaxLoss: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Karşıt zarar</span>
                <b-form-input
                  :value="data.item.positionGuardian.oppositePnlFuseThresholdPct"
                  type="number"
                  size="sm"
                  step="0.01"
                  @change="updateTarget(data.item, { oppositePnlFuseThresholdPct: Number($event) })"
                />
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                class="mt-50"
                @click="tuneTarget(data.item)"
              >
                Check
              </b-button>
            </template>
            <template #cell(recovery)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.recoveryAssistEnabled === true"
                switch
                @change="updateTarget(data.item, { recoveryAssistEnabled: $event })"
              >
                Recovery Assist
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.recoverySameSideFailureFlip !== false"
                switch
                @change="updateTarget(data.item, { recoverySameSideFailureFlip: $event })"
              >
                Aynı yönlü başka edge'i ters aç
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.recoveryAssistMode === 'AUTO_OPEN'"
                switch
                @change="updateTarget(data.item, { recoveryAssistMode: $event ? 'AUTO_OPEN' : 'SUGGEST_ONLY' })"
              >
                Otomatik pozisyon aç
              </b-form-checkbox>
              <div class="guardian-rule-row">
                <span>Risk %</span>
                <b-form-input
                  :value="data.item.positionGuardian.recoveryMaxRiskPct"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateTarget(data.item, { recoveryMaxRiskPct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Cooldown</span>
                <b-form-input
                  :value="data.item.positionGuardian.recoveryCooldownMinutes"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateTarget(data.item, { recoveryCooldownMinutes: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Günlük recovery</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceMaxRecoveryPerDay"
                  type="number"
                  size="sm"
                  min="0"
                  step="1"
                  @change="updateTarget(data.item, { insuranceMaxRecoveryPerDay: Number($event) })"
                />
              </div>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <b-tab title="Kazanç Stratejileri">
        <b-card no-body>
          <b-table
            :items="strategies"
            :fields="strategyFields"
            responsive
            small
            show-empty
            empty-text="Kazanç stratejisi bulunamadı."
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="text-muted font-small-3">
                {{ formatNum(data.item.totalBudget, 0) }} USDT · {{ data.item.totalBots }} bot · {{ data.item.mode }}
              </div>
            </template>
            <template #cell(enabled)="data">
              <b-form-checkbox
                :checked="strategyGuardian(data.item).enabled"
                switch
                @change="updateStrategyGuardian(data.item, { enabled: $event })"
              >
                Position Guardian
              </b-form-checkbox>
            </template>
            <template #cell(authority)="data">
              <b-form-select
                :value="strategyGuardian(data.item).authority"
                :options="authorityOptions"
                size="sm"
                @change="updateStrategyGuardian(data.item, { authority: $event })"
              />
            </template>
            <template #cell(profile)="data">
              <b-form-select
                :value="strategyGuardian(data.item).profile"
                :options="profileOptions"
                size="sm"
                @change="updateStrategyGuardian(data.item, { profile: $event })"
              />
            </template>
            <template #cell(mode)="data">
              <b-badge variant="light-primary">
                {{ modeLabel(strategyExitMode(data.item)) }}
              </b-badge>
            </template>
            <template #cell(coordinate)="data">
              <b-form-checkbox
                :checked="strategyGuardian(data.item).coordinateMfeControls"
                switch
                @change="updateStrategyGuardian(data.item, { coordinateMfeControls: $event })"
              >
                Koruma çakışmasını önle
              </b-form-checkbox>
            </template>
            <template #cell(cost)="data">
              <b-form-checkbox
                :checked="strategyGuardian(data.item).walletAware"
                switch
                @change="updateStrategyGuardian(data.item, { walletAware: $event })"
              >
                Cüzdan duyarlı
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).tpPreservation"
                switch
                @change="updateStrategyGuardian(data.item, { tpPreservation: $event })"
              >
                TP koruma
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).oppositePnlFuseEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { oppositePnlFuseEnabled: $event })"
              >
                Karşıt PnL sigortası
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).halfSlSoftExitEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { halfSlSoftExitEnabled: $event })"
              >
                Yarı SL
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).avgWinProtectionEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { avgWinProtectionEnabled: $event })"
              >
                Avg win
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).mfeProgressWatchEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { mfeProgressWatchEnabled: $event })"
              >
                TP ilerleme takibi
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).autoTuneApplyWithoutApproval"
                switch
                @change="updateStrategyGuardian(data.item, { autoTuneApplyWithoutApproval: $event })"
              >
                Oto uygula
              </b-form-checkbox>
            </template>
            <template #cell(profitRules)="data">
              <div class="text-muted font-small-3 mb-50">
                PnL değil, fiyatın lehe ilerleme yüzdesi.
              </div>
              <div class="guardian-rule-row">
                <span>Komisyonu kapat</span>
                <b-form-input
                  :value="strategyGuardian(data.item).breakEvenMoveMfe"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateStrategyGuardian(data.item, { breakEvenMoveMfe: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kârı kilitle</span>
                <b-form-input
                  :value="strategyGuardian(data.item).profitMoveMfe"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateStrategyGuardian(data.item, { profitMoveMfe: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Min kilit</span>
                <b-form-input
                  :value="strategyGuardian(data.item).profitMoveMinBuffer"
                  type="number"
                  size="sm"
                  step="0.01"
                  @change="updateStrategyGuardian(data.item, { profitMoveMinBuffer: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kâr koruma</span>
                <b-form-input
                  :value="strategyGuardian(data.item).minMfeToProtect"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateStrategyGuardian(data.item, { minMfeToProtect: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Geri verme</span>
                <b-form-input
                  :value="strategyGuardian(data.item).maxGivebackPercent"
                  type="number"
                  size="sm"
                  step="1"
                  @change="updateStrategyGuardian(data.item, { maxGivebackPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Avg win hedef</span>
                <b-form-input
                  :value="strategyGuardian(data.item).minProfitCloseAvgWinRatio"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateStrategyGuardian(data.item, { minProfitCloseAvgWinRatio: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>İzlemeye al</span>
                <b-form-input
                  :value="strategyGuardian(data.item).mfeProgressWatchStart"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateStrategyGuardian(data.item, { mfeProgressWatchStart: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Süre dk</span>
                <b-form-input
                  :value="strategyGuardian(data.item).mfeProgressWatchMinutes"
                  type="number"
                  size="sm"
                  step="1"
                  @change="updateStrategyGuardian(data.item, { mfeProgressWatchMinutes: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Beklenen ilerleme</span>
                <b-form-input
                  :value="strategyGuardian(data.item).mfeProgressMinBridgePercent"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateStrategyGuardian(data.item, { mfeProgressMinBridgePercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Küçük zarar</span>
                <b-form-input
                  :value="strategyGuardian(data.item).mfeProgressMaxLoss"
                  type="number"
                  size="sm"
                  step="0.05"
                  @change="updateStrategyGuardian(data.item, { mfeProgressMaxLoss: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Karşıt zarar</span>
                <b-form-input
                  :value="strategyGuardian(data.item).oppositePnlFuseThresholdPct"
                  type="number"
                  size="sm"
                  step="0.01"
                  @change="updateStrategyGuardian(data.item, { oppositePnlFuseThresholdPct: Number($event) })"
                />
              </div>
            </template>
            <template #cell(recovery)="data">
              <b-form-checkbox
                :checked="strategyGuardian(data.item).recoveryAssistEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { recoveryAssistEnabled: $event })"
              >
                Recovery Assist
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).recoverySameSideFailureFlip"
                switch
                @change="updateStrategyGuardian(data.item, { recoverySameSideFailureFlip: $event })"
              >
                Aynı yönlü başka edge'i ters aç
              </b-form-checkbox>
              <b-form-checkbox
                :checked="strategyGuardian(data.item).recoveryAssistMode === 'AUTO_OPEN'"
                switch
                @change="updateStrategyGuardian(data.item, { recoveryAssistMode: $event ? 'AUTO_OPEN' : 'SUGGEST_ONLY' })"
              >
                Otomatik pozisyon aç
              </b-form-checkbox>
              <div class="guardian-rule-row">
                <span>Risk %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).recoveryMaxRiskPct"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateStrategyGuardian(data.item, { recoveryMaxRiskPct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Cooldown</span>
                <b-form-input
                  :value="strategyGuardian(data.item).recoveryCooldownMinutes"
                  type="number"
                  size="sm"
                  step="5"
                  @change="updateStrategyGuardian(data.item, { recoveryCooldownMinutes: Number($event) })"
                />
              </div>
            </template>
            <template #cell(insurance)="data">
              <b-form-checkbox
                :checked="strategyGuardian(data.item).lossInsuranceEnabled"
                switch
                @change="updateStrategyGuardian(data.item, { lossInsuranceEnabled: $event })"
              >
                Zarar sigortası
              </b-form-checkbox>
              <div class="guardian-rule-row">
                <span>Prim %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insurancePremiumPercent"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insurancePremiumPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Hasar ödeme %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceDamageRepaymentPercent"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceDamageRepaymentPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Risk azaltma %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceDamageRiskReductionPercent"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceDamageRiskReductionPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kasa hedefi %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceReserveTargetPercent"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceReserveTargetPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Max hasar</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceMaxOpenDamageUsdt"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceMaxOpenDamageUsdt: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Recovery risk %</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceRecoveryRiskCapPercent"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceRecoveryRiskCapPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Günlük recovery</span>
                <b-form-input
                  :value="strategyGuardian(data.item).insuranceMaxRecoveryPerDay"
                  type="number"
                  size="sm"
                  @change="updateStrategyGuardian(data.item, { insuranceMaxRecoveryPerDay: Number($event) })"
                />
              </div>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <b-tab title="Yakın Takip">
        <b-alert
          variant="info"
          show
        >
          Son 24 saatin net gerçekleşmiş kârını ve güvenli sayılan açık kârı tampon olarak kullanır. Baskıda yeni giriş ve Recovery durur; kazananların kârı kilitlenir, zararlar kâra en yakın olandan temizlenir.
        </b-alert>
        <b-card no-body>
          <b-table
            :items="targets"
            :fields="closeFollowFields"
            responsive
            small
            show-empty
            empty-text="Hedef bulunamadı."
          >
            <template #cell(target)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.status }}
              </div>
            </template>
            <template #cell(mode)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.closeFollowEnabled === true"
                switch
                @change="updateTarget(data.item, { closeFollowEnabled: $event })"
              >
                Yakın Takip
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.closeFollowRequireApproval === true"
                switch
                @change="updateTarget(data.item, { closeFollowRequireApproval: $event })"
              >
                İşlem öncesi onay iste
              </b-form-checkbox>
            </template>
            <template #cell(thresholds)="data">
              <div class="guardian-rule-row">
                <span>Açık kâr güveni %</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowUnrealizedConfidencePct"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowUnrealizedConfidencePct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Girişleri durdur %</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowEntryPausePct"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowEntryPausePct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Zararı temizle %</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowLossUsageTriggerPct"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowLossUsageTriggerPct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Rahatlama hedefi %</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowReleasePct"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowReleasePct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Min sepet kârı</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowMinNetProfitUsdt"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowMinNetProfitUsdt: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Min pozisyon yaşı (dk)</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowMinPositionAgeMinutes"
                  type="number"
                  min="0"
                  step="0.5"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowMinPositionAgeMinutes: Number($event) })"
                />
              </div>
            </template>
            <template #cell(actions)="data">
              <div class="guardian-rule-row">
                <span>Kazananı kilitle %</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowWinnerLockPct"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowWinnerLockPct: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Tur başı max çıkış</span>
                <b-form-input
                  :value="data.item.positionGuardian.closeFollowMaxClosesPerCycle"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { closeFollowMaxClosesPerCycle: Number($event) })"
                />
              </div>
              <b-form-checkbox
                :checked="data.item.positionGuardian.closeFollowCloseOnPositiveBasket !== false"
                switch
                @change="updateTarget(data.item, { closeFollowCloseOnPositiveBasket: $event })"
              >
                Açık sepet artıya dönünce temizle
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.closeFollowProtectWinners !== false"
                switch
                @change="updateTarget(data.item, { closeFollowProtectWinners: $event })"
              >
                Kazanan pozisyonları koru
              </b-form-checkbox>
              <b-form-checkbox
                :checked="data.item.positionGuardian.closeFollowPauseRecovery !== false"
                switch
                @change="updateTarget(data.item, { closeFollowPauseRecovery: $event })"
              >
                Baskıda Recovery durdur
              </b-form-checkbox>
            </template>
            <template #cell(state)="data">
              <template v-if="closeFollowState(data.item.id)">
                <b-badge :variant="closeFollowState(data.item.id).triggerActive ? 'danger' : closeFollowState(data.item.id).entryBlocked ? 'warning' : 'success'">
                  {{ closeFollowState(data.item.id).triggerActive ? 'Müdahale' : closeFollowState(data.item.id).entryBlocked ? 'Yakın takip' : 'Rahat' }}
                </b-badge>
                <b-badge
                  v-if="closeFollowState(data.item.id).recoveryBlocked"
                  variant="light-danger"
                  class="ml-50"
                >
                  Recovery beklemede
                </b-badge>
                <div
                  v-if="closeFollowState(data.item.id).recoveryBlocked"
                  class="text-muted font-small-3 mt-25"
                >
                  Close Follow recovery'yi bekletiyor.
                </div>
                <div class="mt-50">
                  Tampon {{ formatNum(closeFollowState(data.item.id).safeProfitBuffer, 2) }} USDT
                </div>
                <div :class="closeFollowState(data.item.id).lossUsagePct >= data.item.positionGuardian.closeFollowLossUsageTriggerPct ? 'text-danger' : 'text-muted'">
                  Zarar kullanımı {{ formatNum(closeFollowState(data.item.id).lossUsagePct, 0) }}%
                </div>
                <div class="text-muted font-small-3">
                  Real {{ signedNum(closeFollowState(data.item.id).realizedPnl24h, 2) }} · Açık {{ signedNum(closeFollowState(data.item.id).openNetPnl, 2) }}
                </div>
                <div class="text-muted font-small-3">
                  +{{ closeFollowState(data.item.id).positiveCount }} / -{{ closeFollowState(data.item.id).negativeCount }} pozisyon
                </div>
                <div class="font-small-3">
                  {{ closeFollowState(data.item.id).triggerReason }}
                </div>
              </template>
              <span
                v-else
                class="text-muted"
              >
                Veri yok
              </span>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <b-tab title="Zarar Sigortası">
        <b-alert
          variant="info"
          show
        >
          Sigorta gerçek PnL'yi değiştirmez. Kârdan sanal prim ayırır, zararları kasadan ve hasar defterinden izler; açık hasar varken yeni pozisyon riskini azaltır.
        </b-alert>
        <b-card
          no-body
          class="mb-2"
        >
          <b-table
            :items="targets"
            :fields="insuranceTargetFields"
            responsive
            small
            show-empty
            empty-text="Hedef yok."
          >
            <template #cell(target)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.status }}
              </div>
            </template>
            <template #cell(enabled)="data">
              <b-form-checkbox
                :checked="data.item.positionGuardian.lossInsuranceEnabled === true"
                switch
                @change="updateTarget(data.item, { lossInsuranceEnabled: $event })"
              >
                {{ data.item.positionGuardian.lossInsuranceEnabled ? 'Aktif' : 'Kapalı' }}
              </b-form-checkbox>
            </template>
            <template #cell(allocation)="data">
              <div class="guardian-rule-row">
                <span>Kârdan prim %</span>
                <b-form-input
                  :value="data.item.positionGuardian.insurancePremiumPercent"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insurancePremiumPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Hasar ödeme %</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceDamageRepaymentPercent"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceDamageRepaymentPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Kasa hedefi %</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceReserveTargetPercent"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceReserveTargetPercent: Number($event) })"
                />
              </div>
            </template>
            <template #cell(safety)="data">
              <div class="guardian-rule-row">
                <span>Hasar alarmı USDT</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceMaxOpenDamageUsdt"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceMaxOpenDamageUsdt: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Risk azaltma %</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceDamageRiskReductionPercent"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceDamageRiskReductionPercent: Number($event) })"
                />
              </div>
            </template>
            <template #cell(recovery)="data">
              <div class="guardian-rule-row">
                <span>Recovery risk %</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceRecoveryRiskCapPercent"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceRecoveryRiskCapPercent: Number($event) })"
                />
              </div>
              <div class="guardian-rule-row">
                <span>Günlük max</span>
                <b-form-input
                  :value="data.item.positionGuardian.insuranceMaxRecoveryPerDay"
                  type="number"
                  size="sm"
                  @change="updateTarget(data.item, { insuranceMaxRecoveryPerDay: Number($event) })"
                />
              </div>
            </template>
            <template #cell(account)="data">
              <template v-if="insuranceAccount(data.item.id)">
                <strong class="text-success">
                  Kasa {{ formatNum(insuranceAccount(data.item.id).reserveBalance, 2) }} USDT
                </strong>
                <div :class="insuranceAccount(data.item.id).openDamage > 0 ? 'text-danger' : 'text-muted'">
                  Açık hasar {{ formatNum(insuranceAccount(data.item.id).openDamage, 2) }} USDT
                </div>
                <div class="text-muted font-small-3">
                  Ödenen {{ formatNum(insuranceAccount(data.item.id).totalRepaid, 2) }} USDT
                </div>
              </template>
              <span
                v-else
                class="text-muted"
              >
                Henüz hareket yok
              </span>
            </template>
          </b-table>
        </b-card>

        <b-card no-body>
          <b-table
            :items="insuranceEvents"
            :fields="insuranceEventFields"
            responsive
            small
            show-empty
            empty-text="Sigorta hareketi yok."
          >
            <template #cell(source)="data">
              <strong>{{ data.item.targetName }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.botName }}
              </div>
            </template>
            <template #cell(type)="data">
              <b-badge :variant="insuranceEventVariant(data.item.eventType)">
                {{ insuranceEventLabel(data.item.eventType) }}
              </b-badge>
            </template>
            <template #cell(amounts)="data">
              PnL {{ signedNum(data.item.tradePnl, 2) }}
              <div class="text-muted font-small-3">
                Prim {{ formatNum(data.item.premium, 2) }} · Kasa {{ formatNum(data.item.reserveUsed, 2) }} · Ödeme {{ formatNum(data.item.damageRepaid, 2) }}
              </div>
            </template>
            <template #cell(balance)="data">
              Kasa {{ formatNum(data.item.reserveBalanceAfter, 2) }}
              <div class="text-muted font-small-3">
                Hasar {{ formatNum(data.item.openDamageAfter, 2) }}
              </div>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <b-tab title="Karar Geçmişi">
        <b-card no-body>
          <b-table
            :items="decisions"
            :fields="decisionFields"
            responsive
            small
            show-empty
            empty-text="Guardian kararı yok."
          >
            <template #cell(action)="data">
              <strong>{{ data.item.action }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.authority }}
              </div>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="decisionVariant(data.item.status)">
                {{ data.item.status }}
              </b-badge>
              <div
                v-if="data.item.error"
                class="text-danger font-small-3"
              >
                {{ data.item.error }}
              </div>
            </template>
            <template #cell(metrics)="data">
              MFE {{ formatNum(data.item.mfePct, 2) }}% · PnL {{ signedNum(data.item.pnlUsdt, 2) }}
              <div class="text-muted font-small-3">
                {{ formatNum(data.item.ageMinutes, 0) }} dk
              </div>
              <div
                v-if="data.item.oldSlPrice || data.item.newSlPrice"
                class="text-muted font-small-3"
              >
                SL {{ formatNum(data.item.oldSlPrice, 6) }} → {{ formatNum(data.item.newSlPrice, 6) }}
              </div>
              <div
                v-if="data.item.exchangeOrderUpdated"
                class="text-success font-small-3"
              >
                Borsa SL güncellendi
              </div>
              <div
                v-if="data.item.recoveryPositionId"
                class="text-success font-small-3"
              >
                Recovery pozisyon #{{ data.item.recoveryPositionId }} · bot #{{ data.item.recoveryBotId }}
              </div>
            </template>
            <template #cell(reason)="data">
              <span>{{ data.item.reason || '-' }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="data.item.status === 'SUGGESTED' && !isRecoveryDecision(data.item)"
                size="sm"
                variant="outline-primary"
                @click="applyDecision(data.item)"
              >
                Uygula
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCardHeader, BCol, BFormCheckbox, BFormInput, BFormSelect, BRow, BTab, BTable, BTabs,
} from 'bootstrap-vue'
import {
  strategySafetyGovernorConflictSummary as buildStrategySafetyGovernorConflictSummary,
  strategySafetyGovernorDecisionReason as buildStrategySafetyGovernorCoordinationDecision,
  strategySafetyGovernorCoordinationState as buildStrategySafetyGovernorCoordinationState,
  strategySafetyGovernorCoordinationSummary as buildStrategySafetyGovernorCoordinationSummary,
} from '@/utils/strategySafetyGovernorCoordination'
import { exitGovernorModeLabel, resolveExitGovernorMode } from '@/utils/exitGovernorModes'

export default {
  name: 'PositionGuardianIndex',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BRow,
    BTab,
    BTable,
    BTabs,
  },
  data() {
    return {
      loading: false,
      pollTimer: null,
      tuneResult: null,
      positionFields: [
        { key: 'position', label: 'Pozisyon' },
        { key: 'pnl', label: 'PnL' },
        { key: 'excursion', label: 'MFE / MAE' },
        { key: 'risk', label: 'Risk' },
        { key: 'config', label: 'Guardian' },
        { key: 'actions', label: '' },
      ],
      targetFields: [
        { key: 'name', label: 'Hedef' },
        { key: 'enabled', label: 'Aktif' },
        { key: 'authority', label: 'Yetki' },
        { key: 'profile', label: 'Profil' },
        { key: 'mode', label: 'Mode' },
        { key: 'coordinate', label: 'MFE' },
        { key: 'cost', label: 'Maliyet' },
        { key: 'profitRules', label: 'Kâr Eşikleri' },
        { key: 'recovery', label: 'Recovery' },
      ],
      strategyFields: [
        { key: 'name', label: 'Strateji' },
        { key: 'enabled', label: 'Aktif' },
        { key: 'authority', label: 'Yetki' },
        { key: 'profile', label: 'Profil' },
        { key: 'mode', label: 'Mode' },
        { key: 'coordinate', label: 'MFE' },
        { key: 'cost', label: 'Maliyet' },
        { key: 'profitRules', label: 'Kâr Eşikleri' },
        { key: 'recovery', label: 'Recovery' },
        { key: 'insurance', label: 'Sigorta' },
      ],
      decisionFields: [
        { key: 'action', label: 'Aksiyon' },
        { key: 'status', label: 'Durum' },
        { key: 'metrics', label: 'Metrik' },
        { key: 'reason', label: 'Sebep' },
        { key: 'actions', label: '' },
      ],
      insuranceTargetFields: [
        { key: 'target', label: 'Hedef' },
        { key: 'enabled', label: 'Sigorta' },
        { key: 'allocation', label: 'Kasa ve Ödeme' },
        { key: 'safety', label: 'Hasar Modu' },
        { key: 'recovery', label: 'Recovery' },
        { key: 'account', label: 'Güncel Durum' },
      ],
      closeFollowFields: [
        { key: 'target', label: 'Hedef' },
        { key: 'mode', label: 'Mod' },
        { key: 'thresholds', label: 'Tampon Eşikleri' },
        { key: 'actions', label: 'Aksiyonlar' },
        { key: 'state', label: 'Canlı Durum' },
      ],
      insuranceEventFields: [
        { key: 'source', label: 'Kaynak' },
        { key: 'type', label: 'Hareket' },
        { key: 'amounts', label: 'Tutarlar' },
        { key: 'balance', label: 'Son Durum' },
        { key: 'reason', label: 'Sebep' },
      ],
      authorityOptions: [
        { value: 'AUTO_FULL', text: 'Tam otomatik' },
        { value: 'AUTO_STOP_ONLY', text: 'Sadece SL otomatik' },
        { value: 'SUGGEST_CONFIRM', text: 'Öneri + onay' },
      ],
      profileOptions: [
        { value: 'BALANCED', text: 'Dengeli' },
        { value: 'PROTECTIVE', text: 'Koruyucu' },
        { value: 'AGGRESSIVE', text: 'Agresif' },
      ],
    }
  },
  computed: {
    overview() { return this.$store.getters['positionGuardian/overview'] || { positions: [], counts: {}, closeFollow: [] } },
    positions() { return this.overview.positions || [] },
    counts() { return this.overview.counts || {} },
    targets() { return this.$store.getters['positionGuardian/targets'] || [] },
    decisions() { return this.$store.getters['positionGuardian/decisions'] || [] },
    strategies() { return this.$store.getters['profitStrategies/dataList'] || [] },
    insurance() { return this.$store.getters['positionGuardian/insurance'] || { accounts: [], events: [] } },
    insuranceAccounts() { return this.insurance.accounts || [] },
    insuranceEvents() { return this.insurance.events || [] },
    closeFollowStates() { return this.overview.closeFollow || [] },
    getStrategySafetyGovernorCoordinationSummary() {
      return config => buildStrategySafetyGovernorCoordinationSummary(config)
    },
    getStrategySafetyGovernorCoordinationDecision() {
      return config => buildStrategySafetyGovernorCoordinationDecision(config)
    },
    getStrategySafetyGovernorConflictSummary() {
      return config => buildStrategySafetyGovernorConflictSummary(config)
    },
    getStrategySafetyGovernorCoordinationState() {
      return config => buildStrategySafetyGovernorCoordinationState(config)
    },
  },
  created() {
    this.refreshAll()
    this.pollTimer = setInterval(this.refreshSoft, 10000)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    async refreshAll() {
      this.loading = true
      try {
        await Promise.all([
          this.$store.dispatch('positionGuardian/getOverview'),
          this.$store.dispatch('positionGuardian/getTargets'),
          this.$store.dispatch('positionGuardian/getDecisions'),
          this.$store.dispatch('positionGuardian/getInsurance'),
          this.$store.dispatch('profitStrategies/getDataList', {}),
        ])
      } finally {
        this.loading = false
      }
    },
    refreshSoft() {
      this.$store.dispatch('positionGuardian/getOverview')
      this.$store.dispatch('positionGuardian/getDecisions')
      this.$store.dispatch('positionGuardian/getInsurance')
    },
    toggleTarget(target, enabled) {
      this.updateTarget(target, { enabled })
    },
    updateTarget(target, patch) {
      this.$store.dispatch('positionGuardian/updateTarget', { id: target.id, body: patch })
    },
    async tuneTarget(target) {
      const apply = target.positionGuardian.autoTuneApplyWithoutApproval === true
      const result = await this.$store.dispatch('positionGuardian/tuneTarget', { id: target.id, body: { apply } })
      this.tuneResult = {
        targetName: target.name,
        ...result,
      }
    },
    strategyGuardian(strategy) {
      const config = strategy.config || {}
      const guardian = config.positionGuardian || {}
      return this.normalizeGuardian(guardian)
    },
    strategyExitMode(strategy) {
      return resolveExitGovernorMode(strategy?.config?.exitGovernor?.mode)
    },
    async updateStrategyGuardian(strategy, patch) {
      const previousConfig = strategy.config || {}
      const config = this.sanitizeStrategyConfig(previousConfig)
      const positionGuardian = this.normalizeGuardian({ ...this.strategyGuardian(strategy), ...patch })
      const nextConfig = {
        ...config,
        positionGuardian,
      }
      this.$set(strategy, 'config', nextConfig)
      const body = {
        name: strategy.name,
        description: strategy.description || null,
        isActive: strategy.isActive !== false,
        totalBudget: Number(strategy.totalBudget || 0),
        totalBots: Number(strategy.totalBots || 0),
        leverage: Number(strategy.leverage || 1),
        mode: strategy.mode,
        market: strategy.market,
        edgeKind: strategy.edgeKind,
        tpPerc: Number(strategy.tpPerc || 0),
        slPerc: Number(strategy.slPerc || 0),
        profitTarget: this.optionalNumber(strategy.profitTarget, 5),
        profitTargetIsPct: strategy.profitTargetIsPct !== false,
        maxLossLimit: this.optionalNumber(strategy.maxLossLimit, null),
        config: nextConfig,
      }
      try {
        const updated = await this.$store.dispatch('profitStrategies/update', { id: strategy.id, body })
        if (updated && updated.config) this.$set(strategy, 'config', updated.config)
      } catch (error) {
        this.$set(strategy, 'config', previousConfig)
      }
    },
    sanitizeStrategyConfig(config) {
      const next = { ...(config || {}) }
      if (next.targetSignalPoolFilters === null) delete next.targetSignalPoolFilters
      if (next.autoCalculation === null) delete next.autoCalculation
      if (next.pnlHealthAutoTune === null) delete next.pnlHealthAutoTune
      if (next.positionGuardian === null) delete next.positionGuardian
      return next
    },
    normalizeGuardian(value) {
      const authorityValues = ['SUGGEST_CONFIRM', 'AUTO_STOP_ONLY', 'AUTO_FULL']
      const profileValues = ['PROTECTIVE', 'BALANCED', 'AGGRESSIVE']
      const authority = authorityValues.includes(value.authority) ? value.authority : 'AUTO_FULL'
      const profile = profileValues.includes(value.profile) ? value.profile : 'BALANCED'
      return {
        enabled: value.enabled !== false,
        authority,
        profile,
        minMfeToProtect: this.optionalNumber(value.minMfeToProtect, 0.30),
        maxGivebackPercent: this.optionalNumber(value.maxGivebackPercent, 45),
        softExitAfterMinutes: this.optionalNumber(value.softExitAfterMinutes, 45),
        softExitMaxLoss: this.optionalNumber(value.softExitMaxLoss, 0.20),
        profitExitMinPnl: this.optionalNumber(value.profitExitMinPnl, 0.15),
        tightenOnMomentumLoss: value.tightenOnMomentumLoss !== false,
        blockSameSideWhileNegative: value.blockSameSideWhileNegative !== false,
        coordinateMfeControls: value.coordinateMfeControls !== false,
        preMfeDamageControl: value.preMfeDamageControl !== false,
        preMfeAfterMinutes: this.optionalNumber(value.preMfeAfterMinutes, 20),
        preMfeMaxMfe: this.optionalNumber(value.preMfeMaxMfe, 0.15),
        preMfeSoftExitLoss: this.optionalNumber(value.preMfeSoftExitLoss, 0.45),
        halfSlSoftExitEnabled: value.halfSlSoftExitEnabled !== false,
        halfSlSoftExitRatio: this.optionalNumber(value.halfSlSoftExitRatio, 50),
        mfeProgressWatchEnabled: value.mfeProgressWatchEnabled !== false,
        mfeProgressWatchStart: this.optionalNumber(value.mfeProgressWatchStart, 0.30),
        mfeProgressWatchMinutes: this.optionalNumber(value.mfeProgressWatchMinutes, 15),
        mfeProgressMinBridgePercent: this.optionalNumber(value.mfeProgressMinBridgePercent, 55),
        mfeProgressMaxLoss: this.optionalNumber(value.mfeProgressMaxLoss, 0.10),
        minNetProfitBuffer: this.optionalNumber(value.minNetProfitBuffer, 0.03),
        breakEvenMoveMfe: this.optionalNumber(value.breakEvenMoveMfe, 0.55),
        profitMoveMfe: this.optionalNumber(value.profitMoveMfe, 0.75),
        profitMoveMinBuffer: this.optionalNumber(value.profitMoveMinBuffer, 0.10),
        profitMoveDynamicRatio: this.optionalNumber(value.profitMoveDynamicRatio, 25),
        autoTuneApplyWithoutApproval: value.autoTuneApplyWithoutApproval === true,
        walletAware: value.walletAware !== false,
        walletPressureDrawdownPct: this.optionalNumber(value.walletPressureDrawdownPct, 0.50),
        walletPressureLossMultiplier: this.optionalNumber(value.walletPressureLossMultiplier, 0.75),
        walletPressureTimeMultiplier: this.optionalNumber(value.walletPressureTimeMultiplier, 0.50),
        tpPreservation: value.tpPreservation !== false,
        minTpDistanceForProfitClose: this.optionalNumber(value.minTpDistanceForProfitClose, 0.25),
        minTpProgressForProfitClose: this.optionalNumber(value.minTpProgressForProfitClose, 50),
        oppositePnlFuseEnabled: value.oppositePnlFuseEnabled === true,
        oppositePnlFuseThresholdPct: this.optionalNumber(value.oppositePnlFuseThresholdPct, 0.96),
        avgWinProtectionEnabled: value.avgWinProtectionEnabled !== false,
        avgWinLookbackTrades: this.optionalNumber(value.avgWinLookbackTrades, 30),
        minAvgWinSamples: this.optionalNumber(value.minAvgWinSamples, 3),
        minProfitCloseAvgWinRatio: this.optionalNumber(value.minProfitCloseAvgWinRatio, 70),
        recoveryAssistEnabled: value.recoveryAssistEnabled === true,
        recoveryAssistMode: value.recoveryAssistMode === 'AUTO_OPEN' ? 'AUTO_OPEN' : 'SUGGEST_ONLY',
        recoverySameSideFailureFlip: value.recoverySameSideFailureFlip !== false,
        recoveryAllowSameSymbolHedge: value.recoveryAllowSameSymbolHedge === true,
        recoveryMinSameSideFailures: this.optionalNumber(value.recoveryMinSameSideFailures, 1),
        recoveryWeaknessLossPct: this.optionalNumber(value.recoveryWeaknessLossPct, 0.15),
        recoveryWeaknessLookbackBars: this.optionalNumber(value.recoveryWeaknessLookbackBars, 12),
        recoveryMaxRiskPct: this.optionalNumber(value.recoveryMaxRiskPct, 25),
        recoveryCooldownMinutes: this.optionalNumber(value.recoveryCooldownMinutes, 30),
        recoveryMinCandidateScore: this.optionalNumber(value.recoveryMinCandidateScore, 0.20),
        recoveryCandidateLimit: this.optionalNumber(value.recoveryCandidateLimit, 3),
        lossInsuranceEnabled: value.lossInsuranceEnabled === true,
        insurancePremiumPercent: this.optionalNumber(value.insurancePremiumPercent, 20),
        insuranceDamageRepaymentPercent: this.optionalNumber(value.insuranceDamageRepaymentPercent, 35),
        insuranceReserveTargetPercent: this.optionalNumber(value.insuranceReserveTargetPercent, 8),
        insuranceMaxOpenDamageUsdt: this.optionalNumber(value.insuranceMaxOpenDamageUsdt, 100),
        insuranceDamageRiskReductionPercent: this.optionalNumber(value.insuranceDamageRiskReductionPercent, 25),
        insuranceRecoveryRiskCapPercent: this.optionalNumber(value.insuranceRecoveryRiskCapPercent, 15),
        insuranceMaxRecoveryPerDay: this.optionalNumber(value.insuranceMaxRecoveryPerDay, 1),
        closeFollowEnabled: value.closeFollowEnabled === true,
        closeFollowUnrealizedConfidencePct: this.optionalNumber(value.closeFollowUnrealizedConfidencePct, 60),
        closeFollowEntryPausePct: this.optionalNumber(value.closeFollowEntryPausePct, 50),
        closeFollowLossUsageTriggerPct: this.optionalNumber(value.closeFollowLossUsageTriggerPct, 70),
        closeFollowReleasePct: this.optionalNumber(value.closeFollowReleasePct, 50),
        closeFollowMinNetProfitUsdt: this.optionalNumber(value.closeFollowMinNetProfitUsdt, 5),
        closeFollowWinnerLockPct: this.optionalNumber(value.closeFollowWinnerLockPct, 70),
        closeFollowCloseOnPositiveBasket: value.closeFollowCloseOnPositiveBasket !== false,
        closeFollowProtectWinners: value.closeFollowProtectWinners !== false,
        closeFollowPauseRecovery: value.closeFollowPauseRecovery !== false,
        closeFollowRequireApproval: value.closeFollowRequireApproval === true,
        closeFollowMaxClosesPerCycle: this.optionalNumber(value.closeFollowMaxClosesPerCycle, 3),
        closeFollowMinPositionAgeMinutes: this.optionalNumber(value.closeFollowMinPositionAgeMinutes, 2),
      }
    },
    optionalNumber(value, fallback) {
      const n = Number(value)
      if (Number.isFinite(n)) return n
      return fallback
    },
    modeLabel(mode) {
      return exitGovernorModeLabel(mode)
    },
    applyDecision(item) {
      this.$store.dispatch('positionGuardian/applyDecision', item.id)
    },
    closePosition(item) {
      this.$store.dispatch('positionGuardian/closePosition', item.position.id)
    },
    decisionVariant(status) {
      if (status === 'APPLIED') return 'success'
      if (status === 'FAILED') return 'danger'
      if (status === 'SKIPPED') return 'warning'
      return 'primary'
    },
    isRecoveryDecision(item) {
      return ['RECOVERY_SUGGEST', 'RECOVERY_SKIPPED', 'SAME_SIDE_WEAKNESS_DETECTED'].includes(item.action)
    },
    insuranceAccount(targetId) {
      return this.insuranceAccounts.find(item => Number(item.id_targets) === Number(targetId)) || null
    },
    closeFollowState(targetId) {
      return this.closeFollowStates.find(item => Number(item.targetId) === Number(targetId)) || null
    },
    insuranceEventVariant(type) {
      if (type === 'LOSS') return 'danger'
      if (type === 'DAMAGE_REPAYMENT') return 'primary'
      if (type === 'PREMIUM') return 'success'
      return 'secondary'
    },
    insuranceEventLabel(type) {
      const labels = {
        LOSS: 'Hasar',
        DAMAGE_REPAYMENT: 'Hasar ödemesi',
        PREMIUM: 'Prim',
        PROFIT: 'Kâr',
      }
      return labels[type] || type
    },
    formatNum(value, digits = 2) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '-'
      return n.toFixed(digits)
    },
    signedNum(value, digits = 2) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '-'
      return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}`
    },
  },
}
</script>

<style scoped>
.guardian-rule-row {
  display: grid;
  grid-template-columns: minmax(116px, 1fr) minmax(72px, 96px);
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.25rem;
}

.guardian-rule-row span {
  color: #6e6b7b;
  font-size: 0.75rem;
}
</style>
